// accessControlUtils.js - COMPLETELY FIXED VERSION
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Network error' }));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

// Simple URL builder - just adds /access prefix
const buildApiUrl = (endpoint) => {
  return `${API_BASE_URL}${endpoint}`;
};

// URL builder with business parameter
const buildApiUrlWithBusiness = (endpoint, selectedBusiness = null) => {
  const baseUrl = buildApiUrl(endpoint);
  if (selectedBusiness) {
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}business=${encodeURIComponent(selectedBusiness)}`;
  }
  return baseUrl;
};

const buildApiUrlWithEmail = (endpoint, email = null) => {
  const baseUrl = buildApiUrl(endpoint);
  if (email) {
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}email=${encodeURIComponent(email)}`;
  }
  return baseUrl;
};

// BUSINESS FUNCTIONS
export const getBusinesses = async () => {
  try {
    const response = await fetch(buildApiUrl('/access/getBusinesses'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching businesses:', error);
    throw error;
  }
};

// USER FUNCTIONS
export const getUsers = async (selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness('/access/getUsers', selectedBusiness);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const getUser = async (userId, selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness(`/access/getUser/${userId}`, selectedBusiness);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

export const inviteUser = async (userData, selectedBusiness = null) => {
  try {
    const requestData = selectedBusiness 
      ? { ...userData, business: selectedBusiness }
      : userData;

    const response = await fetch(buildApiUrl('/access/inviteUser'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error inviting user:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData, selectedBusiness = null) => {
  try {
    const requestData = selectedBusiness 
      ? { ...userData, business: selectedBusiness }
      : userData;

    const response = await fetch(buildApiUrl(`/access/updateUser/${userId}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const removeUser = async (userId, selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness(`/access/removeUser/${userId}`, selectedBusiness);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error removing user:', error);
    throw error;
  }
};

// SITE FUNCTIONS
export const getSites = async (selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness('/access/getSites', selectedBusiness);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching sites:', error);
    throw error;
  }
};

export const getSiteApplications = async (site = null, selectedBusiness = null) => {
  try {
    let url = buildApiUrlWithBusiness('/access/getSiteApplications', selectedBusiness);
    
    if (site) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}site=${encodeURIComponent(site)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching site applications:', error);
    throw error;
  }
};

// STATS FUNCTIONS
export const getUserStats = async (selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness('/access/getUserStats', selectedBusiness);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

export const updateLastActive = async (userId, selectedBusiness = null) => {
  try {
    const requestData = selectedBusiness 
      ? { business: selectedBusiness }
      : {};

    const response = await fetch(buildApiUrl(`/access/updateLastActive/${userId}`), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error updating last active:', error);
    throw error;
  }
};

// INVITATION MANAGEMENT FUNCTIONS
export const getPendingInvitations = async (selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness('/access/getPendingInvitations', selectedBusiness);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching pending invitations:', error);
    throw error;
  }
};

export const getInvitationByToken = async (token) => {
  try {
    const response = await fetch(buildApiUrl(`/access/getInvitation/${token}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error fetching invitation:', error);
    throw error;
  }
};

export const confirmInvitation = async (token) => {
  try {
    const response = await fetch(buildApiUrl('/access/confirmInvitation'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error confirming invitation:', error);
    throw error;
  }
};

export const cancelInvitation = async (invitationId, selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness(`/access/cancelInvitation/${invitationId}`, selectedBusiness);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    throw error;
  }
};

export const resendInvitation = async (invitationId, selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness(`/access/resendInvitation/${invitationId}`, selectedBusiness);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error resending invitation:', error);
    throw error;
  }
};

export const sendInvitationReminder = async (invitationId, selectedBusiness = null) => {
  try {
    const url = buildApiUrlWithBusiness(`/access/sendReminder/${invitationId}`, selectedBusiness);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error sending reminder:', error);
    throw error;
  }
};

export const testEmailConfiguration = async () => {
  try {
    const response = await fetch(buildApiUrl('/test-email'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error testing email configuration:', error);
    throw error;
  }
};

export const sendTestInvitation = async (testData) => {
  try {
    const response = await fetch(buildApiUrl('/test-invitation-email'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(testData),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error sending test invitation:', error);
    throw error;
  }
};

export const cleanupExpiredInvitations = async () => {
  try {
    const response = await fetch(buildApiUrl('/access/cleanup-expired-invitations'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error cleaning up expired invitations:', error);
    throw error;
  }
};

// HELPER FUNCTIONS
export const validateUserPermissions = (currentUserRole, targetAction) => {
  const permissions = {
    admin: ['view', 'edit', 'delete', 'invite', 'manage_invitations'],
    supervisor: ['view'],
    technician: []
  };

  return permissions[currentUserRole]?.includes(targetAction) || false;
};

export const formatUserForAPI = (formData) => {
  return {
    name: formData.name?.trim(),
    email: formData.email?.toLowerCase().trim(),
    role: formData.role,
    sites: formData.selectedSites || []
  };
};

export const getErrorMessage = (error) => {
  if (error.message.includes('already exists')) {
    return 'A user with this email already exists.';
  }
  if (error.message.includes('pending invitation')) {
    return 'There is already a pending invitation for this email address.';
  }
  if (error.message.includes('not found')) {
    return 'User not found.';
  }
  if (error.message.includes('business configuration')) {
    return 'Business configuration not found. Please contact support.';
  }
  if (error.message.includes('Invalid business selection')) {
    return 'Invalid business selection. Please select a valid business.';
  }
  if (error.message.includes('expired')) {
    return 'This invitation has expired.';
  }
  if (error.message.includes('already confirmed')) {
    return 'This invitation has already been confirmed.';
  }
  return error.message || 'An unexpected error occurred.';
};

export const bulkUpdateUsers = async (userUpdates, selectedBusiness = null) => {
  try {
    const promises = userUpdates.map(({ id, data }) => updateUser(id, data, selectedBusiness));
    return await Promise.all(promises);
  } catch (error) {
    console.error('Error in bulk update:', error);
    throw error;
  }
};

export const filterUsers = (users, searchTerm, roleFilter) => {
  return users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });
};

export const filterInvitations = (invitations, searchTerm, statusFilter) => {
  return invitations.filter(invitation => {
    const matchesSearch = searchTerm === '' || 
      invitation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invitation.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || invitation.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
};

export const updateUserActivity = async (email) => {
  try {
    const apiUrl = buildApiUrlWithEmail('/access/updateLastActive', email);
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to update last active');
    }
    
    const result = await response.json();
  } catch (error) {
    console.error('Error updating last active:', error);
  }
};

// Default export
export default {
  getBusinesses,
  getUsers,
  getUser,
  inviteUser,
  updateUser,
  removeUser,
  getSites,
  getSiteApplications,
  getUserStats,
  updateLastActive,
  getPendingInvitations,
  getInvitationByToken,
  confirmInvitation,
  cancelInvitation,
  resendInvitation,
  sendInvitationReminder,
  testEmailConfiguration,
  sendTestInvitation,
  cleanupExpiredInvitations,
  validateUserPermissions,
  formatUserForAPI,
  getErrorMessage,
  bulkUpdateUsers,
  filterUsers,
  filterInvitations,
  updateUserActivity
};